import { TextFieldProps } from "@mui/material/TextField";
import classNames from "classnames";
import React, { useContext, useState } from "react";

import InfoIcon from "assets/icons/Info.svg?react";

import { InputDisabledContext } from "components/InputState/InputDisabledContext";
import Tooltip from "components/Tooltip";

import styles from "./styles.module.scss";

type Props = {
  inputRootClassName?: string;
  labelClassName?: string;
  testId?: string;
  showAsterisk?: boolean;
  mainField?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  labelTooltip?: React.ReactNode;
  readOnly?: boolean;
  spellCheck?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
} & Omit<TextFieldProps, "variant">;

export const TextInput = React.forwardRef<HTMLInputElement, Props>(
  (props, ref) => {
    const {
      error,
      required,
      autoFocus,
      autoComplete = "off",
      helperText,
      label,
      onFocus,
      onBlur,
      onChange,
      onKeyDown,
      value,
      name,
      type,
      className,
      labelClassName,
      inputRootClassName,
      testId,
      placeholder,
      showAsterisk,
      mainField,
      multiline,
      startAdornment,
      endAdornment,
      labelTooltip,
      readOnly,
      spellCheck,
      rows,
      minRows,
      maxRows,
    } = props;
    const isDisabled = useContext(InputDisabledContext);
    const disabled = props.disabled || isDisabled;
    const [inputFocused, setInputFocused] = useState(false);

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setInputFocused(true);
      onFocus && onFocus(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setInputFocused(false);
      onBlur && onBlur(event);
    };

    const inputProps = {};
    if (value !== undefined) {
      Object.assign(inputProps, { value });
    }

    const inputComponentProps = {
      className: styles.input,
      ref,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onChange,
      onKeyDown,
      required,
      autoFocus,
      autoComplete,
      disabled,
      name,
      type,
      "data-testid": testId,
      placeholder,
      "main-field": mainField ? "true" : undefined,
      readOnly,
      spellCheck,
      ...(multiline ? { rows, minRows, maxRows } : {}),
      ...inputProps,
    };

    const inputMarkup = React.createElement(
      multiline ? "textarea" : "input",
      inputComponentProps
    );

    return (
      <div className={classNames(styles.root, className)}>
        {label && (
          <div className={classNames(styles.label, labelClassName)}>
            {label}
            {showAsterisk && required && (
              <span className={styles.required}>*</span>
            )}
            {labelTooltip && (
              <Tooltip
                onClick={e => e.stopPropagation()}
                title={labelTooltip}
                classes={{ tooltip: styles.tooltip }}
                arrow
              >
                <span className={styles.infoIconContainer}>
                  <InfoIcon className={styles.infoIcon} />
                </span>
              </Tooltip>
            )}
          </div>
        )}
        <div
          className={classNames(styles.inputRoot, inputRootClassName, {
            [styles.inputError]: !!error,
            [styles.focused]: inputFocused,
            [styles.disabled]: disabled,
            [styles.startAdornment]: !!startAdornment,
            [styles.endAdornment]: !!endAdornment,
          })}
        >
          <div className={styles.inputWrapper}>
            {startAdornment}
            {inputMarkup}
            {endAdornment}
          </div>
          {error && (
            <p
              data-testid={`${testId}-error-message`}
              className={styles.helperText}
            >
              {helperText}
            </p>
          )}
        </div>
      </div>
    );
  }
);
